import React from 'react';
import { Empty } from 'antd';
import { Profil, ServiceType, User, UserInfo, UserFilter, Users } from 'types';
import StyledTable from 'components/organisms/styled-table';
import ListModifier from 'components/organisms/list-modifier';
import { useIntl } from 'common/config/i18n';
import PaginationBlock from 'components/molecules/pagination';

type Props = {
  data?: Users;
  userInfo?: UserInfo;
  profileById: (id?: string) => Profil | undefined;
  onClickEdit: (record: User) => void;
  onClickDelete: (record: User) => void;
  canRead: boolean;
  filterValues: UserFilter;
  serviceTypes: ServiceType[];
  fetchListAction: (params: UserFilter) => void;
};

const getServiceTypeNames = (serviceTypes: ServiceType[], trainTypeIds?: number[]) => {
  if (!trainTypeIds?.length) {
    return '';
  }
  const idSet = new Set(trainTypeIds);

  return serviceTypes
    .filter((item) => idSet.has(item.id))
    .map((item) => item.serviceTypeName)
    .join(', ');
};

const UserList: React.FC<Props> = ({
  data,
  userInfo,
  profileById,
  onClickDelete,
  onClickEdit,
  canRead,
  filterValues,
  serviceTypes,
  fetchListAction,
}: Props) => {
  const { t } = useIntl();

  const columns = [
    {
      title: t('table', 'COLUMN_USER_LAST_NAME'),
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: t('table', 'COLUMN_USER_FIRST_NAME'),
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: t('table', 'COLUMN_USER_EMAIL'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('table', 'COLUMN_USER_PROFILE'),
      key: 'userRoles',
      render: (record: User) => {
        return <div>{profileById(record?.profileID?.toString())?.name}</div>;
      },
    },
    {
      title: t('table', 'COLUMN_USER_CIRCULATION'),
      key: 'circulation',
      render: (record: User) => {
        return <div>{getServiceTypeNames(serviceTypes, record?.trainTypeIDs)}</div>;
      },
    },
    {
      title: t('table', 'COLUMN_USER_GROUP'),
      key: 'group',
      render: (record: User) => {
        return <div>{record?.groupName}</div>;
      },
    },
    {
      key: 'modifier',
      title: t('table', 'COLUMN_EDIT_DELETE'),
      render: (record: User) => {
        return (
          <ListModifier
            onDelete={() => onClickDelete(record)}
            onEdit={() => onClickEdit(record)}
            hideDeleteButton={!canRead || userInfo?.id === record.id}
            hideEditButton={!canRead && userInfo?.id !== record.id}
          />
        );
      },
    },
  ];

  return (
    <>
      <StyledTable
        textAlign="left"
        pagination={false}
        dataSource={data?.result}
        columns={columns}
        rowKey="id"
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} /> }}
      />
      <PaginationBlock
        data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
        onChange={(page: number, pageSize?: number) => {
          fetchListAction({
            ...filterValues,
            pageNumber: page,
            maxResultPage: pageSize,
          });
        }}
      />
    </>
  );
};

export default UserList;
